/**
 * File Management Models
 * DTOs and Interfaces for file/folder management APIs
 */

/**
 * Folder Interface - Matches backend structure
 */
export interface Folder {
  id: number;
  nameEn: string;
  nameAr: string;
  descriptionEn?: string;
  descriptionAr?: string;
  parentId: number | null;
  createdAt: string;
  updatedAt?: string | null;
  childFolderCount: number;
  fileCount: number;
}

/**
 * Language Enum
 * 1 = English, 2 = Arabic
 */
export enum LanguageEnum {
  English = 1,
  Arabic = 2
}

/**
 * Document Type Enum
 * 1=SOP, 2=Form, 3=Program, 4=Policy, 5=Manual, 6=Report
 */
export enum DocumentTypeEnum {
  SOP = 1,
  Form = 2,
  Program = 3,
  Policy = 4,
  Manual = 5,
  Report = 6
}

/**
 * File Interface - Matches backend FileResponse structure
 */
export interface FileItem {
  id: number;
  titleEn: string;
  titleAr: string;
  folderId?: number | null;
  descriptionEn?: string | null;
  descriptionAr?: string | null;
  
  /**
   * Document type as integer ID (1-6)
   * 1=SOP, 2=Form, 3=Program, 4=Policy, 5=Manual, 6=Report
   */
  documentTypeId?: number | null;
  
  /**
   * Language as integer ID (1-2)
   * 1=English, 2=Arabic
   */
  languageId?: number | null;
  
  type: string;
  size: string;
  uploadDate?: string | null;
  createdBy?: number | null;
  createdAt: string;
  updatedAt?: string | null;
  
  /**
   * Unique storage identifier for the file
   */
  storageId: string;
}

/**
 * Pagination Info
 */
export interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/**
 * Root Folders API Response
 * GET /externalbackend/api/v1/external/folders/roots?iamUserId={iamUserId}&pageNumber={pageNumber}&pageSize={pageSize}
 */
export interface RootFoldersResponse {
  success: boolean;
  message: string;
  data: {
    folders: Folder[];
  };
  errors: string[] | null;
}

/**
 * Browse Folder API Response
 * GET /externalbackend/api/v1/external/folders/{folderId}/browse?iamUserId={iamUserId}&pageNumber={pageNumber}&pageSize={pageSize}
 */
export interface BrowseFolderResponse {
  success: boolean;
  message: string;
  data: {
    subFolders: Folder[];
    files: FileItem[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  errors: string[] | null;
}

/**
 * Enum for language selection
 */
export enum Language {
  EN = 'en',
  AR = 'ar'
}

/**
 * Helper class for file/folder display
 */
export class FileManagementHelper {
  /**
   * Get localized folder name
   */
  static getFolderName(folder: Folder, language: Language | string = 'en'): string {
    if (!folder) return '';
    return language === 'ar' ? folder.nameAr : folder.nameEn;
  }

  /**
   * Get localized folder description
   */
  static getFolderDescription(folder: Folder, language: Language | string = 'en'): string {
    if (!folder) return '';
    return language === 'ar' ? folder.descriptionAr || folder.descriptionEn || '' : folder.descriptionEn || '';
  }

  /**
   * Get localized file title
   */
  static getFileTitle(file: FileItem, language: Language | string = 'en'): string {
    if (!file) return '';
    return language === 'ar' ? file.titleAr : file.titleEn;
  }

  /**
   * Get localized file description
   */
  static getFileDescription(file: FileItem, language: Language | string = 'en'): string {
    if (!file) return '';
    return language === 'ar' ? file.descriptionAr || file.descriptionEn || '' : file.descriptionEn || '';
  }

  /**
   * Get file icon based on type
   */
  static getFileIcon(type: string): string {
    const icons: Record<string, string> = {
      PDF: 'bi-file-earmark-pdf',
      DOCX: 'bi-file-earmark-word',
      XLSX: 'bi-file-earmark-spreadsheet',
      PPTX: 'bi-file-earmark-presentation',
      MP4: 'bi-file-earmark-play',
      default: 'bi-file-earmark'
    };
    return icons[type?.toUpperCase()] || icons['default'];
  }
}
